import type { ReactNode } from 'react'
import { BackLink } from '@/components/play'
import { SiteFooter } from '@/components/SiteFooter'
import { WorkspaceHeader } from '@/components/workspace/WorkspaceHeader'
import { cn } from '@/lib/utils'

interface EventWorkspaceShellProps {
  backTo?: string
  /** The name of the page `backTo` goes to, shown on the back link: the event's name for the event. */
  backLabel?: string
  title?: ReactNode
  meta?: ReactNode
  actions?: ReactNode
  /** Optional view-switcher rendered as a sub-nav row beneath the header. */
  workspaceSwitcher?: ReactNode
  children: ReactNode
  className?: string
  contentClassName?: string
  /** The one footer, SiteFooter, after the page. A page that fills the screen, as the AI Producer canvas does, passes
   *  false: it ends at the screen's edge. */
  footer?: boolean
}

/**
 * A page of the event workspace that isn't the workspace itself, such as Sponsors and the AI Producer canvas: the
 * workspace's quiet header (WorkspaceHeader: the logo back to the events, Host workspace and the account menu), then
 * the page's one `main` (its back link, title and actions, the view switcher and the content) and SiteFooter. App.tsx
 * lists these routes as Play surfaces, so it adds no shell of its own. `main` is a flex column, so a screen-high page
 * (`className="flex h-screen flex-col overflow-hidden"`) can give its content the rest (`flex min-h-0 flex-1`).
 */
export function EventWorkspaceShell({
  backTo,
  backLabel,
  title,
  meta,
  actions,
  workspaceSwitcher,
  children,
  className,
  contentClassName,
  footer = true,
}: EventWorkspaceShellProps) {
  return (
    <div className={cn('play-theme relative min-h-screen bg-transparent', className)}>
      <WorkspaceHeader label="Host workspace" />
      <main className="flex min-h-0 flex-1 flex-col">
        <header className="relative z-10 border-b border-neutral-4 bg-neutral-1 px-5 py-3">
          {/* Its own row above the title, as on every page, so the title keeps the width. */}
          {backTo && <BackLink to={backTo} className="mb-xs">{backLabel}</BackLink>}
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              {title && <div className="min-w-0">{title}</div>}
              {meta && (
                <div className="mt-1 flex items-center gap-2 text-xs text-neutral-7">
                  {meta}
                </div>
              )}
            </div>

            {actions && (
              <div className="flex shrink-0 items-center gap-2">
                {actions}
              </div>
            )}
          </div>
        </header>

        {workspaceSwitcher && (
          <div className="relative z-10 border-b border-neutral-4 bg-neutral-1 px-5 py-2">
            {workspaceSwitcher}
          </div>
        )}

        <div className={cn('relative', contentClassName)}>
          {children}
        </div>
      </main>
      {footer && <SiteFooter />}
    </div>
  )
}
